/**
 * pose-server の複数視点推定結果をボーン単位でマージする純粋ロジック、および推定結果の
 * bones を Map<VRMHumanBoneName, THREE.Quaternion> に変換する純粋ロジック（toPoseMap）。
 * three（THREE.Quaternion）・@pixiv/three-vrm（VRMHumanBoneName）以外には依存しない —
 * DOM API・`../api/client`（import.meta.env・localStorage 等ブラウザ専用の参照を持つ）の
 * どちらにも依存しない — `serverPose.ts` から分離したのは、pose-factory（Node/vitest環境、
 * 独立パッケージ）がこれらの関数を相対importでそのまま参照するため（コピーはしない。本番
 * （Upload.tsx経由）と量産ライン（pose-factory）で変換ロジックが食い違うと、同じ入力から
 * 違うVRMAが出てしまうため参照先を一本化している）。frontend側のimport元は変える必要が
 * 無いよう、`serverPose.ts` がここを re-export する。
 */
import * as THREE from 'three';
import { VRMHumanBoneName } from '@pixiv/three-vrm';

/** pose-server が返す1ボーン分のクォータニオン（ローカル回転）。 */
export interface PoseQuat {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** pose-server から1視点分の推定結果として返る形。boneConfidences は複数視点マージ時の採用判定に使う。 */
export interface ServerPoseResult {
  bones: Record<string, PoseQuat>;
  rootTranslation?: { x: number; y: number; z: number };
  boneConfidences?: Record<string, number>;
}

/**
 * mergeMultiViewPoses の足切り閾値（度）の既定値。根拠は mergeMultiViewPoses のdocコメント参照。
 * pose-factory/src/estimate.ts が保存用メタデータ（views[].used/deviationDeg）を組み立てる際、
 * mergeMultiViewPoses に暗黙で使わせている閾値と同じ値を明示的に参照できるようexportしている
 * （マジックナンバーの二重管理を避けるため）。
 */
export const DEFAULT_MAX_DEVIATION_DEG = 20;

/**
 * 2つの単位クォータニオンがなす回転角（度）。q と -q は同じ回転を表す（クォータニオンの
 * 二重被覆）ため、内積の絶対値を使う。ここで絶対値を取らないと、実質同じ姿勢なのに
 * 「180°反対向き」と誤判定してしまう。浮動小数点誤差で |dot| がわずかに1を超えることが
 * あるため、Math.acos に渡す前に1でクランプする（クランプ無しだと NaN になる）。
 */
export function quatAngleDeg(a: PoseQuat, b: PoseQuat): number {
  const dot = a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
  const clamped = Math.min(1, Math.abs(dot));
  return (2 * Math.acos(clamped) * 180) / Math.PI;
}

/**
 * base と other に共通して存在するボーンについて quatAngleDeg を取り、その中央値（度）を返す。
 * 平均ではなく中央値にしているのは、1〜2本の推定ミス（大外れボーン）に引きずられず、
 * 「視点全体として base とどれだけ食い違っているか」を頑健に見るため。
 * 共通ボーンが1本も無い場合は Infinity を返す（＝比較不能なので selectConsistentViews で
 * 必ず足切りされる）。
 */
export function medianDeviationDeg(base: ServerPoseResult, other: ServerPoseResult): number {
  const diffs: number[] = [];
  for (const name of Object.keys(base.bones)) {
    const otherQuat = other.bones[name];
    if (!otherQuat) continue;
    diffs.push(quatAngleDeg(base.bones[name], otherQuat));
  }
  if (diffs.length === 0) return Infinity;

  diffs.sort((x, y) => x - y);
  const mid = Math.floor(diffs.length / 2);
  return diffs.length % 2 === 0 ? (diffs[mid - 1] + diffs[mid]) / 2 : diffs[mid];
}

/**
 * views のうち、views[0]（front）と姿勢が食い違いすぎる視点を足切りする。
 * - views[0] は乖離度に関わらず必ず kept に入れる（hips/rootTranslation の基準であり、
 *   これを捨てると全身の向きが決まらないため）。
 * - views[1] 以降は medianDeviationDeg(views[0], view) が maxDeviationDeg 以下のものだけ kept に入れる。
 *   ちょうど閾値と同じ場合は採用する（<=）。
 * - views が空の場合は kept/dropped とも空を返す（views[0] が存在しないため）。
 */
export function selectConsistentViews(
  views: ServerPoseResult[],
  maxDeviationDeg: number,
): { kept: number[]; dropped: { index: number; deviationDeg: number }[] } {
  if (views.length === 0) {
    return { kept: [], dropped: [] };
  }

  const base = views[0];
  const kept: number[] = [0];
  const dropped: { index: number; deviationDeg: number }[] = [];

  for (let i = 1; i < views.length; i++) {
    const deviationDeg = medianDeviationDeg(base, views[i]);
    if (deviationDeg <= maxDeviationDeg) {
      kept.push(i);
    } else {
      dropped.push({ index: i, deviationDeg });
    }
  }

  return { kept, dropped };
}

/**
 * 複数視点（複数枚の画像）から得た pose-server の推定結果を、ボーン単位でマージする純関数。
 * - views が空なら throw する。1件ならそのまま返す。
 * - views.length >= 2 のとき、まず selectConsistentViews で「views[0]（front）と姿勢が
 *   食い違いすぎる視点」を足切りしてから、以下のボーン統合を行う（options.maxDeviationDeg 参照）。
 * - 出力 bones のキーは足切り後に残った views の bones キーの和集合。
 * - hips は views[0]（最初に追加した画像）の値のみを採用する。カメラ視点によって向きが変わる
 *   ボーンのため、信頼度に関わらず views[0] を優先する。rootTranslation も同様に views[0] のものだけを使う。
 * - hips 以外のボーンは、そのボーンを持つ view のうち boneConfidences[bone] が最大のものを採用する。
 *   confidence が無い view は -Infinity 扱い（＝confidence を持つ view があればそちらを優先）。
 *   どの view にも confidence が無いボーンは、そのボーンを持つ最初の view（足切り後の views の並び順）の値に
 *   フォールバックする（boneConfidences 未対応の旧 backend との互換用）。
 * - どの視点が採用されたか（ボーン名→view index）、および足切りで捨てた視点（view index→乖離度）は
 *   console.debug に出す（戻り値には含めない）。
 *
 * @param options.maxDeviationDeg 足切りの閾値（度）。既定値20は実測（GEM-Xに同一ポーズの
 *   6視点を通し、front基準でボーン回転のクォータニオン角度差の中央値を測定）に基づく:
 *   front と一致している「良い視点」は中央値4〜16°、自己遮蔽などで壊れた視点は24〜35°に
 *   分離していたため、その間の20°を境目にした。
 *
 *   **なぜ信頼度（boneConfidences）だけでは駄目か**: 推定器は「自信満々に間違える」ことが
 *   実測で確認されている。例えば「片膝をつく」の side_right（真横90°視点）は boneConfidence
 *   0.910 と高いにもかかわらず、52本中31本が front と30°超ずれていた（横向きは自己遮蔽で
 *   関節を誤認識しやすいが、推定器自身はそれを検知できていない）。信頼度だけでボーンごとに
 *   採用視点を選ぶ現行方式は、この「自信満々な誤り」をそのまま採用してしまう。視点単位で
 *   front との整合性を見る足切りを別途入れているのはこのため。
 *
 *   Infinity を渡すと足切りを無効化できる（＝従来どおり全視点を使う）。
 *
 * **本番Webアプリへの影響**: この関数は pose-factory 専用ではなく、`frontend/src/pages/Upload.tsx`
 * （ユーザーが複数視点画像をアップロードしてポーズ推定するUI）からも呼ばれている。既定値20を
 * 有効にしたことで、Upload.tsx 側の挙動も変わる（frontと食い違う視点は自動的に無視されるように
 * なる）。これは意図した変更 — 矛盾した視点を混ぜて壊れた骨格を作るより、frontに近い（＝一貫性のある）
 * 骨格を優先する方が安全 — であり、Upload.tsx 側の呼び出しコードの変更は不要（第2引数省略で既定値が効く）。
 */
export function mergeMultiViewPoses(
  views: ServerPoseResult[],
  options?: { maxDeviationDeg?: number },
): ServerPoseResult {
  if (views.length === 0) {
    throw new Error('マージ対象の推定結果がありません。');
  }
  if (views.length === 1) {
    return views[0];
  }

  const maxDeviationDeg = options?.maxDeviationDeg ?? DEFAULT_MAX_DEVIATION_DEG;
  // originalIndices[i] は effectiveViews[i] が元の views 配列で何番目だったかを覚えておく
  // （足切り後も provenance ログを元の view index で出せるようにするため）。
  let effectiveViews = views;
  let originalIndices = views.map((_, i) => i);
  if (views.length >= 2) {
    const { kept, dropped } = selectConsistentViews(views, maxDeviationDeg);
    if (dropped.length > 0) {
      console.debug(
        'mergeMultiViewPoses: front と食い違う視点を除外しました（view index → 乖離度[deg]）',
        dropped.map((d) => ({ index: d.index, deviationDeg: d.deviationDeg })),
      );
    }
    effectiveViews = kept.map((i) => views[i]);
    originalIndices = kept;
  }

  const base = effectiveViews[0];
  const boneNames = new Set<string>();
  for (const view of effectiveViews) {
    for (const name of Object.keys(view.bones)) boneNames.add(name);
  }

  const bones: Record<string, PoseQuat> = {};
  const provenance: Record<string, number> = {};

  for (const name of boneNames) {
    if (name === 'hips') {
      if (base.bones.hips) {
        bones.hips = base.bones.hips;
        provenance.hips = originalIndices[0];
      }
      continue;
    }

    let bestIndex = -1;
    let bestConfidence = -Infinity;
    for (let i = 0; i < effectiveViews.length; i++) {
      const quat = effectiveViews[i].bones[name];
      if (!quat) continue;
      const confidence = effectiveViews[i].boneConfidences?.[name] ?? -Infinity;
      // bestIndex === -1 の間は最初に見つかった view を仮採用しておく。
      // こうすると「全 view が confidence 無し（= 全部 -Infinity）」のケースで、
      // 自然に「そのボーンを持つ最初の view」にフォールバックする。
      if (bestIndex === -1 || confidence > bestConfidence) {
        bestConfidence = confidence;
        bestIndex = i;
      }
    }
    if (bestIndex !== -1) {
      bones[name] = effectiveViews[bestIndex].bones[name];
      provenance[name] = originalIndices[bestIndex];
    }
  }

  console.debug('mergeMultiViewPoses: 採用した視点（ボーン→view index）', provenance);

  return {
    bones,
    rootTranslation: base.rootTranslation,
  };
}

const VALID_BONE_NAMES = new Set<string>(Object.values(VRMHumanBoneName));

/**
 * サーバー推論結果の bones（VRMHumanBoneName 文字列キー）を、既存の動画パイプライン
 * （poseSolver.ts の solvePose）と同じ Map<VRMHumanBoneName, THREE.Quaternion> に変換する。
 * ホワイトリスト外のキー（バックエンド側でも検証済みだが二重防御）は console.warn で
 * スキップし、1件も有効なボーンが無ければ throw する。
 */
export function toPoseMap(bones: Record<string, PoseQuat>): Map<VRMHumanBoneName, THREE.Quaternion> {
  const pose = new Map<VRMHumanBoneName, THREE.Quaternion>();
  for (const [name, q] of Object.entries(bones)) {
    if (!VALID_BONE_NAMES.has(name)) {
      console.warn(`toPoseMap: 未知のボーン名をスキップしました: ${name}`);
      continue;
    }
    const quat = new THREE.Quaternion(q.x, q.y, q.z, q.w).normalize();
    pose.set(name as VRMHumanBoneName, quat);
  }
  if (pose.size === 0) {
    throw new Error('有効なボーンデータがありませんでした。');
  }
  return pose;
}
